/**
 * Featured article carousel for sections/educational-section.liquid.
 * Cycles pre-rendered slides and keeps secondary cards in sync with the active article.
 */
class EducationalCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.educational-featured__slide'));
    this.currentIndex = Math.max(
      0,
      this.slides.findIndex((slide) => !slide.hasAttribute('hidden'))
    );
    this.secondaryCount = Number.parseInt(this.dataset.secondaryCount, 10) || 0;
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.autoplaySpeed = (Number.parseInt(this.dataset.autoplaySpeed, 10) || 5) * 1000;
    this.cardsContainer = this.closest('.educational-section__inner')?.querySelector('[data-educational-cards]');

    this.#onPrevious = () => this.go(-1);
    this.#onNext = () => this.go(1);
    this.#onKeydown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.go(-1);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.go(1);
      }
    };
    this.#onMouseEnter = () => this.stopAutoplay();
    this.#onMouseLeave = () => this.startAutoplay();
    this.#onPointerDown = () => this.stopAutoplay();
    this.#onFocusIn = () => this.stopAutoplay();
    this.#onFocusOut = (event) => {
      if (!this.contains(event.relatedTarget)) this.startAutoplay();
    };
    this.#onVisibilityChange = () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    };

    this.querySelector('[data-action="previous"]')?.addEventListener('click', this.#onPrevious);
    this.querySelector('[data-action="next"]')?.addEventListener('click', this.#onNext);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('mouseenter', this.#onMouseEnter);
    this.addEventListener('mouseleave', this.#onMouseLeave);
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);

    this.updateSecondary();
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.querySelector('[data-action="previous"]')?.removeEventListener('click', this.#onPrevious);
    this.querySelector('[data-action="next"]')?.removeEventListener('click', this.#onNext);
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('mouseenter', this.#onMouseEnter);
    this.removeEventListener('mouseleave', this.#onMouseLeave);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#onFocusOut);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    this.stopAutoplay();
  }

  /** @type {() => void} */
  #onPrevious = () => {};

  /** @type {() => void} */
  #onNext = () => {};

  /** @type {(event: KeyboardEvent) => void} */
  #onKeydown = () => {};

  /** @type {() => void} */
  #onMouseEnter = () => {};

  /** @type {() => void} */
  #onMouseLeave = () => {};

  /** @type {() => void} */
  #onPointerDown = () => {};

  /** @type {() => void} */
  #onFocusIn = () => {};

  /** @type {(event: FocusEvent) => void} */
  #onFocusOut = () => {};

  /** @type {() => void} */
  #onVisibilityChange = () => {};

  autoplayEnabled = false;

  autoplaySpeed = 5000;

  autoplayTimer = null;

  /**
   * @param {number} delta - Direction to move (-1 previous, 1 next)
   */
  go(delta) {
    if (this.slides.length <= 1) return;

    this.slides[this.currentIndex]?.setAttribute('hidden', '');

    this.currentIndex = (this.currentIndex + delta + this.slides.length) % this.slides.length;

    this.slides[this.currentIndex]?.removeAttribute('hidden');
    this.updateSecondary();
  }

  startAutoplay() {
    if (!this.autoplayEnabled || this.slides.length <= 1 || document.hidden) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.stopAutoplay();
    this.autoplayTimer = window.setInterval(() => this.go(1), this.autoplaySpeed);
  }

  stopAutoplay() {
    if (!this.autoplayTimer) return;
    window.clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  updateSecondary() {
    if (!this.cardsContainer) return;

    const featuredId = this.slides[this.currentIndex]?.dataset.articleId;
    const cards = Array.from(this.cardsContainer.querySelectorAll('[data-article-id]'));
    let shown = 0;

    for (const card of cards) {
      if (card.dataset.articleId === featuredId) {
        card.hidden = true;
        continue;
      }

      card.hidden = shown >= this.secondaryCount;
      if (!card.hidden) shown += 1;
    }
  }
}

if (!customElements.get('educational-carousel')) {
  customElements.define('educational-carousel', EducationalCarousel);
}
